<script setup lang="ts">
  defineOptions({ name: 'LiveNowCard' })

  import { computed, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { ChevronRightIcon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'

  import CPText from '@ui/components/CPText.vue'

  import EventRegisteredBadge from '../atoms/EventRegisteredBadge.vue'
  import { toEventCoverObjectPosition } from '../../types/events-hub'
  import { getLiveProgressParts } from '../../utils/map-events-management-event'
  import type { EventsHubCard } from '../../types/events-hub'

  const props = defineProps({
    event: {
      type: Object as PropType<EventsHubCard>,
      required: true,
    },
    idPrefix: {
      type: String,
      default: 'live-now',
    },
  })

  const emit = defineEmits<{
    openEvent: [eventId: string]
  }>()

  const { t } = useI18n()
  const showCoverImage = computed(() => Boolean(props.event.imageUrl?.trim()))
  const coverPosition = computed(() => toEventCoverObjectPosition(props.event.imageFocalPoint))
  const coverStyle = computed(() => (coverPosition.value ? { '--event-cover-object-position': coverPosition.value } : undefined))

  const progressText = computed(() => {
    const parts = getLiveProgressParts(props.event.startAt ?? '', props.event.endAt ?? '', new Date(), props.event.timeZone)
    if (!parts) return ''
    if (parts.type === 'day') {
      return t('clientportal.events.liveDayProgress', { current: parts.current, total: parts.total })
    }
    if (parts.hours > 0 && parts.minutes > 0) {
      return t('clientportal.events.liveEndsInHoursMinutes', { hours: parts.hours, minutes: parts.minutes })
    }
    if (parts.hours > 0) {
      return t('clientportal.events.liveEndsInHours', { count: parts.hours })
    }
    return t('clientportal.events.liveEndsInMinutes', { count: parts.minutes })
  })

  const openEvent = () => {
    emit('openEvent', props.event.id)
  }

  const handleKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Enter' && event.key !== ' ') return
    event.preventDefault()
    openEvent()
  }
</script>

<template>
  <article
    :id="createElementId(idPrefix, event.id, 'card')"
    class="live-now-card flex w-full flex-col bg-background group"
    role="button"
    tabindex="0"
    :aria-label="event.title"
    @click="openEvent"
    @keydown="handleKeydown"
  >
    <div class="live-now-card-image-wrapper relative w-full">
      <div class="live-now-card-cover relative aspect-[2/1] w-full overflow-hidden rounded-t-lg" :style="coverStyle">
        <OptimisedImage
          v-if="showCoverImage"
          :id="createElementId(idPrefix, event.id, 'cover')"
          :src="event.imageUrl"
          :alt="event.title"
          source="communities"
          :use-raw-url="true"
          gradient-mode="always"
          class="h-full w-full"
          :img-class="['object-cover transition-all duration-300', 'group-hover:scale-105'].join(' ')"
          fallback-aspect="aspect-[2/1]"
          loading="eager"
          fetchpriority="high"
        />
      </div>
    </div>

    <div class="live-now-card-body relative z-10 flex flex-1 flex-col gap-1.5 px-3 py-3">
      <div class="flex min-w-0 items-center gap-2">
        <div :id="createElementId(idPrefix, event.id, 'live-badge')" class="live-now-card-badge inline-flex shrink-0 items-center gap-1.5">
          <span class="live-now-card-badge-dot" aria-hidden="true" />
          <CPText size="xs" weight="bold" html-tag="span">
            {{ t('clientportal.events.live') }}
          </CPText>
        </div>
        <EventRegisteredBadge v-if="event.isRegistered" :id-prefix="idPrefix" :event-id="event.id" />
      </div>
      <CPText
        :id="createElementId(idPrefix, event.id, 'title')"
        size="lg"
        weight="bold"
        html-tag="h3"
        class="m-0 min-w-0 truncate leading-6 text-body line-clamp-1"
      >
        {{ event.title }}
      </CPText>
      <CPText
        v-if="event.locationLabel"
        :id="createElementId(idPrefix, event.id, 'place')"
        size="md"
        weight="normal"
        class="m-0 truncate text-700"
      >
        {{ event.locationLabel }}
      </CPText>
      <div class="flex items-center gap-1.5">
        <CPText :id="createElementId(idPrefix, event.id, 'progress')" size="md" weight="bold" html-tag="span" class="text-body">
          <template v-if="progressText">{{ progressText }} {{ t('clientportal.events.metaDot') }}</template>
          {{ t('clientportal.events.joinNow') }}
        </CPText>
        <ChevronRightIcon class="h-3.5 w-3.5 shrink-0 text-700" aria-hidden="true" />
      </div>
    </div>
  </article>
</template>

<style scoped>
  .live-now-card {
    border-radius: 8px;
    border: 1px solid var(--cp-border-500);
    cursor: pointer;
    transition: all 0.3s ease;
  }

  .live-now-card-cover {
    background: var(--gray-200, #eaecf0);
  }

  .live-now-card-cover :deep(img) {
    object-fit: cover;
    object-position: var(--event-cover-object-position, center);
  }

  .live-now-card-badge {
    background: var(--error-600, #d92d20);
    color: #fff;
    border-radius: 4px;
    padding: 3px 8px;
  }

  .live-now-card-badge-dot {
    width: 6px;
    height: 6px;
    border-radius: 999px;
    background: #fff;
    animation: live-now-pulse 1.6s ease-in-out infinite;
  }

  .live-now-card:hover,
  .live-now-card:focus-visible {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px color-mix(in srgb, var(--cp-brand-font) 12%, transparent);
    z-index: 20;
    border-color: var(--cp-border-500);
  }

  @keyframes live-now-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.35;
    }
  }
</style>
